/**
 * MinimalStateMachine - Idempotent state management for speech engines
 * 
 * Features:
 * - 7 core states: idle → acquiring_mic → ready → listening → endpointed → stopping → error
 * - No same-state transitions (no-op with debug line)
 * - All transitions carry a reason
 * - Serialized transitions through single queue
 * - AbortController support for clean cancellation
 */

export const MinimalStates = {
    IDLE: 'idle',
    ACQUIRING_MIC: 'acquiring_mic',
    READY: 'ready', // KWS armed, ASR off
    LISTENING: 'listening', // ASR active after KWS detection
    ENDPOINTED: 'endpointed',
    STOPPING: 'stopping',
    ERROR: 'error'
};

export const TransitionReasons = {
    // Start flow
    START_REQUESTED: 'start_requested',
    MIC_ACQUIRED: 'mic_acquired',
    KWS_ARMED: 'kws_armed', // KWS is ready and listening
    KWS_DETECTED: 'kws_detected', // Wake word detected
    LISTENING_STARTED: 'listening_started',
    VAD_SPEECH_START: 'vad_speech_start',
    
    // Stop flow
    STOP_REQUESTED: 'stop_requested',
    LISTENING_ENDED: 'listening_ended',
    STOPPING_COMPLETE: 'stopping_complete',
    
    // Error flow
    ERROR_OCCURRED: 'error_occurred',
    ERROR_RECOVERED: 'error_recovered',
    
    // External events
    VAD_SPEECH_END: 'vad_speech_end',
    SPEECH_ENGINE_STATE_CHANGE: 'speech_engine_state_change',
    MANUAL_STOP: 'manual_stop',
    ENGINE_SWITCH: 'engine_switch',
    CLEANUP: 'cleanup',
    RESET: 'reset'
};

export class MinimalStateMachine {
    constructor(options = {}) {
        this.currentState = MinimalStates.IDLE;
        this.previousState = null;
        this.transitionQueue = [];
        this.isProcessingQueue = false;
        this.abortController = null;
        this.onStateChange = null;
        this.stateHistory = [];
        this.maxHistorySize = options.maxHistorySize || 20;
        this.currentRunId = 0;
        this.pendingStart = false;
        
        // Development logging
        this.isDev = process.env.NODE_ENV === 'development';
        
        if (this.isDev) {
            console.log(`🔄 MinimalStateMachine: Created in state ${this.currentState}`);
        }
    }

    /**
     * Get current state
     */
    getCurrentState() {
        return this.currentState;
    }

    /**
     * Get previous state
     */
    getPreviousState() {
        return this.previousState;
    }

    /**
     * Check if in a specific state
     */
    isInState(state) {
        return this.currentState === state;
    }

    /**
     * Check if currently idle
     */
    isIdle() {
        return this.currentState === MinimalStates.IDLE;
    }

    /**
     * Check if currently listening
     */
    isListening() {
        return this.currentState === MinimalStates.LISTENING;
    }

    /**
     * Check if in error state
     */
    isInError() {
        return this.currentState === MinimalStates.ERROR;
    }

    /**
     * Check if state machine is busy (not idle)
     */
    isBusy() {
        return this.currentState !== MinimalStates.IDLE;
    }

    /**
     * Queue a state transition (serialized)
     */
    async transitionTo(newState, reason, context = {}) {
        return new Promise((resolve, reject) => {
            this.transitionQueue.push({
                newState,
                reason,
                context,
                resolve,
                reject,
                timestamp: Date.now()
            });
            
            this.processQueue();
        });
    }

    /**
     * Process the transition queue (serialized)
     */
    async processQueue() {
        if (this.isProcessingQueue || this.transitionQueue.length === 0) {
            return;
        }

        this.isProcessingQueue = true;

        while (this.transitionQueue.length > 0) {
            const transition = this.transitionQueue.shift();
            
            try {
                await this.executeTransition(transition);
                transition.resolve();
            } catch (error) {
                transition.reject(error);
            }
        }

        this.isProcessingQueue = false;
    }

    /**
     * Execute a single transition
     */
    async executeTransition(transition) {
        const { newState, reason, context } = transition;
        const oldState = this.currentState;

        // Check if same state transition
        if (oldState === newState) {
            if (this.isDev) {
                console.log(`🔄 MinimalStateMachine: Same-state transition ${oldState} → ${newState} (${reason}) - no-op`);
            }
            return;
        }

        // Check if transition is valid
        if (!this.isValidTransition(oldState, newState)) {
            const error = new Error(`Invalid transition from ${oldState} to ${newState} (${reason})`);
            console.error('🔄 MinimalStateMachine:', error.message);
            throw error;
        }

        // Execute transition
        this.previousState = oldState;
        this.currentState = newState;

        // Record in history
        this.recordStateHistory(oldState, newState, reason, context);

        // Log transition
        if (this.isDev) {
            console.log(`🔄 MinimalStateMachine: ${oldState} → ${newState} (${reason})`);
        }

        // Notify listeners
        if (this.onStateChange) {
            this.onStateChange({
                from: oldState,
                to: newState,
                current: this.currentState,
                reason,
                context,
                timestamp: Date.now()
            });
        }
    }

    /**
     * Check if a transition is valid
     */
    isValidTransition(fromState, toState) {
        // Special case: idle → ready only allowed if pendingStart is true
        if (fromState === MinimalStates.IDLE && toState === MinimalStates.READY) {
            return this.pendingStart;
        }
        
        const validTransitions = {
            [MinimalStates.IDLE]: [
                MinimalStates.ACQUIRING_MIC,
                MinimalStates.ERROR,
                MinimalStates.STOPPING  // Allow stopping from idle for cleanup
            ],
            [MinimalStates.ACQUIRING_MIC]: [
                MinimalStates.READY,
                MinimalStates.ERROR,
                MinimalStates.IDLE,
                MinimalStates.STOPPING  // Allow stopping during mic acquisition
            ],
            [MinimalStates.READY]: [
                MinimalStates.LISTENING, // KWS detected, start ASR
                MinimalStates.STOPPING,
                MinimalStates.ERROR,
                MinimalStates.IDLE
            ],
            [MinimalStates.LISTENING]: [
                MinimalStates.ENDPOINTED,
                MinimalStates.STOPPING,
                MinimalStates.ERROR,
                MinimalStates.IDLE
            ],
            [MinimalStates.ENDPOINTED]: [
                MinimalStates.READY, // Return to KWS armed state
                MinimalStates.LISTENING,
                MinimalStates.STOPPING,
                MinimalStates.ERROR,
                MinimalStates.IDLE
            ],
            [MinimalStates.STOPPING]: [
                MinimalStates.IDLE,
                MinimalStates.ERROR
            ],
            [MinimalStates.ERROR]: [
                MinimalStates.IDLE,
                MinimalStates.ACQUIRING_MIC,
                MinimalStates.STOPPING  // Allow stopping from error for cleanup
            ]
        };

        return validTransitions[fromState]?.includes(toState) || false;
    }

    /**
     * Record state history
     */
    recordStateHistory(fromState, toState, reason, context) {
        this.stateHistory.push({
            from: fromState,
            to: toState,
            reason,
            context,
            timestamp: Date.now()
        });

        // Keep history size manageable
        if (this.stateHistory.length > this.maxHistorySize) {
            this.stateHistory.shift();
        }
    }

    /**
     * Create new AbortController for async operations
     */
    createAbortController() {
        // Cancel previous controller if exists
        if (this.abortController) {
            this.abortController.abort();
        }
        
        this.abortController = new AbortController();
        return this.abortController;
    }

    /**
     * Get current AbortController
     */
    getAbortController() {
        return this.abortController;
    }

    /**
     * Cancel current async operations
     */
    cancelAsyncOperations() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    /**
     * Reset state machine to idle
     */
    async reset(reason = TransitionReasons.RESET) {
        this.cancelAsyncOperations();
        await this.transitionTo(MinimalStates.IDLE, reason);
    }

    /**
     * Get state history
     */
    getStateHistory() {
        return [...this.stateHistory];
    }

    /**
     * Get last transition
     * @returns {Object|null} Last transition object
     */
    getLastTransition() {
        return this.stateHistory.length > 0 ? this.stateHistory[this.stateHistory.length - 1] : null;
    }

    /**
     * Start a new run (increment runId)
     * @returns {number} New runId
     */
    startRun() {
        this.currentRunId++;
        this.pendingStart = true;
        if (this.isDev) {
            console.log(`🔄 MinimalStateMachine: Starting run ${this.currentRunId}`);
        }
        return this.currentRunId;
    }

    /**
     * End current run
     */
    endRun() {
        this.pendingStart = false;
        if (this.isDev) {
            console.log(`🔄 MinimalStateMachine: Ending run ${this.currentRunId}`);
        }
    }

    /**
     * Check if event is from current run
     * @param {number} eventRunId - RunId from event
     * @returns {boolean} True if event is current
     */
    isCurrentRun(eventRunId) {
        return eventRunId === this.currentRunId;
    }

    /**
     * Get current runId
     * @returns {number} Current runId
     */
    getCurrentRunId() {
        return this.currentRunId;
    }

    /**
     * Get state machine status
     */
    getStatus() {
        return {
            currentState: this.currentState,
            previousState: this.previousState,
            isProcessingQueue: this.isProcessingQueue,
            queueLength: this.transitionQueue.length,
            hasAbortController: !!this.abortController,
            historyLength: this.stateHistory.length
        };
    }

    /**
     * Set state change callback
     */
    setStateChangeCallback(callback) {
        this.onStateChange = callback;
    }

    /**
     * Clear state change callback
     */
    clearStateChangeCallback() {
        this.onStateChange = null;
    }

    /**
     * Destroy state machine
     */
    destroy() {
        this.cancelAsyncOperations();
        this.clearStateChangeCallback();
        this.transitionQueue = [];
        this.stateHistory = [];
        this.currentState = MinimalStates.IDLE;
        this.previousState = null;
        
        if (this.isDev) {
            console.log('🔄 MinimalStateMachine: Destroyed');
        }
    }
}
